/**
 * The attempt timeline — the reason this screen exists.
 *
 * The axis spans `startMs … endMs`, and **`startMs` is normally negative**: t=0
 * is `attempts.started_at`, but the phone is paired, placed and used for the
 * paper check and the room scan before that, so it uploads a minute or so of
 * footage at negative offsets. That window is drawn as an explicit "Setup"
 * region to the left of a marked exam-start line rather than being clipped,
 * because if something looks wrong about an attempt, the setup is exactly the
 * part worth watching.
 *
 * Lanes:
 *
 *   1. the webcam track's coverage (recorded runs vs "no signal" holes)
 *   2. the phone track's coverage
 *   3. proctor events, coloured by severity
 *   4. every moment a question was opened (`visit_offsets_ms`), coloured by
 *      whether that question ended up right or wrong — so the stretch of the
 *      attempt where the answers went wrong is visible without reading a
 *      single row of the table underneath
 *
 * Clicking anywhere on the axis — background, event marker, or question tick —
 * seeks the master clock, and therefore both videos, to that instant. Hovering
 * a marker shows what it was.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import type { ProctorSeverity, RecordingSource } from "@exam/shared";
import { PROCTOR_EVENT_LABEL } from "@exam/shared";
import { cn } from "@/lib/cn";
import { formatClock, formatOffset } from "../lib/format";
import { answerStateOf, type AnswerState } from "./QuestionFollower";
import type { AnswerRow, TimelineEvent, Track } from "../data/types";

/** Lane heading, and the subject of the "uploaded nothing" tooltip. */
const SOURCE_LANE: Record<RecordingSource, { label: string; subject: string }> =
  {
    webcam: { label: "Webcam", subject: "The webcam" },
    phone: { label: "Phone", subject: "The phone" },
    screen: { label: "Screen", subject: "The screen recording" },
  };

const SEVERITY_CLASS: Record<ProctorSeverity, string> = {
  critical: "bg-violation",
  warn: "bg-review",
  info: "bg-ink-faint",
};

/**
 * The question lane's colours, which are the same three the rest of the screen
 * grades with: green right, red wrong, grey untouched. `ungraded` borrows the
 * "needs a human" amber rather than inventing a fourth colour.
 */
const ANSWER_CLASS: Record<AnswerState, string> = {
  correct: "bg-answered",
  wrong: "bg-violation",
  unanswered: "bg-ink-faint/50",
  ungraded: "bg-review",
};

const ANSWER_LABEL: Record<AnswerState, string> = {
  correct: "Answered correctly",
  wrong: "Answered wrongly",
  unanswered: "Left unanswered",
  ungraded: "Not graded yet",
};

const SEVERITY_LABEL: Record<ProctorSeverity, string> = {
  critical: "Critical",
  warn: "Warning",
  info: "Info",
};

interface Hover {
  /** Percentage across the axis, so the card follows the marker. */
  left: number;
  title: string;
  lines: string[];
}

export function Timeline({
  startMs,
  endMs,
  tracks,
  events,
  answers,
  timeMs,
  onSeek,
  selectedQuestionId,
  onSelectQuestion,
}: {
  /** Left edge, `<= 0`. Negative means there is pre-exam setup footage. */
  startMs: number;
  endMs: number;
  tracks: Track[];
  events: TimelineEvent[];
  answers: AnswerRow[];
  timeMs: number;
  onSeek: (ms: number) => void;
  selectedQuestionId?: string | null;
  /** The visit that was clicked, not just the question — the caller pins to it. */
  onSelectQuestion?: (questionId: string, offsetMs: number) => void;
}) {
  const axisRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);

  const span = Math.max(1, endMs - startMs);
  /** Position on the axis, 0–100%. Handles a negative left edge. */
  const pct = useCallback(
    (ms: number) => ((ms - startMs) / span) * 100,
    [span, startMs],
  );
  /** A width, which is a difference and must not be re-based on `startMs`. */
  const widthPct = useCallback((ms: number) => (ms / span) * 100, [span]);

  const seekFromEvent = useCallback(
    (clientX: number) => {
      const box = axisRef.current?.getBoundingClientRect();
      if (!box || box.width === 0) return;
      const ratio = (clientX - box.left) / box.width;
      onSeek(startMs + Math.max(0, Math.min(1, ratio)) * span);
    },
    [onSeek, span, startMs],
  );

  const hasSetup = startMs < -999;

  // Question ticks: one per recorded visit, labelled with the paper position.
  // Memoised because this component re-renders on every clock tick.
  const visits = useMemo(
    () =>
      answers.flatMap((answer) =>
        answer.visit_offsets_ms.map((offset, i) => ({
          key: `${answer.question.id}-${i}`,
          question_id: answer.question.id,
          number: answer.order_idx + 1,
          offset_ms: offset,
          visit: i + 1,
          total_visits: answer.visit_offsets_ms.length,
          state: answerStateOf(answer),
        })),
      ),
    [answers],
  );

  return (
    <div className="relative select-none px-3 py-3">
      {/* Ruler */}
      <div className="mb-1 flex items-center justify-between text-2xs text-ink-faint">
        <span className="tabular">{formatOffset(startMs)}</span>
        <span>Click the timeline to jump both cameras to that moment</span>
        <span className="tabular">{formatClock(endMs)}</span>
      </div>

      <div
        ref={axisRef}
        className="relative cursor-pointer"
        role="slider"
        tabIndex={0}
        aria-label="Attempt timeline"
        aria-valuemin={Math.round(startMs / 1000)}
        aria-valuemax={Math.round(endMs / 1000)}
        aria-valuenow={Math.round(timeMs / 1000)}
        aria-valuetext={
          timeMs < 0
            ? `${formatOffset(timeMs)}, before the exam started`
            : formatClock(timeMs)
        }
        onClick={(e) => seekFromEvent(e.clientX)}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            onSeek(Math.max(startMs, timeMs - 5000));
          }
          if (e.key === "ArrowRight") {
            e.preventDefault();
            onSeek(Math.min(endMs, timeMs + 5000));
          }
        }}
        onMouseLeave={() => setHover(null)}
      >
        {/* Phases: the setup window the phone recorded, then the exam itself. */}
        {hasSetup ? (
          <div className="mb-1.5 flex h-4 w-full overflow-hidden rounded-xs border border-border">
            <div
              className="flex items-center overflow-hidden whitespace-nowrap border-r border-border-strong bg-neutral-soft px-1 text-2xs text-ink-muted"
              style={{ width: `${widthPct(-startMs)}%` }}
              onMouseEnter={() =>
                setHover({
                  left: 0,
                  title: "Setup",
                  lines: [
                    `${formatOffset(startMs)} – 00:00`,
                    "Pairing, placement, paper check and room scan",
                  ],
                })
              }
            >
              Setup
            </div>
            <div
              className="flex flex-1 items-center overflow-hidden whitespace-nowrap px-1 text-2xs text-ink-muted"
              onMouseEnter={() =>
                setHover({
                  left: pct(0),
                  title: "Exam",
                  lines: [`00:00 – ${formatClock(endMs)}`],
                })
              }
            >
              Exam
            </div>
          </div>
        ) : null}

        {/* Coverage lanes */}
        {tracks.map((track) => (
          <div key={track.source} className="mb-1">
            <div className="mb-0.5 flex items-baseline gap-1.5">
              <span className="text-2xs text-ink-muted">
                {SOURCE_LANE[track.source].label}
              </span>
              {track.chunks.length === 0 ? (
                <span className="text-2xs text-violation">
                  {/* "nothing recorded" would be false for a device that
                      recorded during an earlier occasion against this same
                      attempt — see `Track.earlier`. */}
                  {track.earlier
                    ? "nothing in this sitting"
                    : "nothing recorded"}
                </span>
              ) : null}
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-xs bg-surface-sunken">
              {track.segments.map((segment, i) => {
                // A hole that ends when the exam begins is not a dropout: that
                // device simply had not started yet. Only real loss reads red.
                const beforeStart =
                  segment.start_ms < 0 && segment.end_ms <= 1500;
                // Footage that is on the server and cannot be decoded. Painted
                // as its own thing: drawn like ordinary coverage it reads as a
                // stretch that was recorded and can be watched, and a reviewer
                // would only find out by clicking it.
                const unplayable =
                  segment.kind === "chunks" && !segment.decodable;
                return (
                  <div
                    key={i}
                    className={cn(
                      "absolute inset-y-0",
                      unplayable
                        ? "bg-violation/25 ring-1 ring-inset ring-violation/50"
                        : segment.kind === "chunks"
                          ? "bg-ink-muted/45"
                          : beforeStart
                            ? "bg-neutral-soft ring-1 ring-inset ring-border-strong"
                            : "bg-violation-soft ring-1 ring-inset ring-violation/40",
                    )}
                    style={{
                      left: `${pct(segment.start_ms)}%`,
                      width: `${Math.max(0.15, widthPct(segment.end_ms - segment.start_ms))}%`,
                    }}
                    onMouseEnter={() =>
                      setHover({
                        left: pct(segment.start_ms),
                        title: unplayable
                          ? "Unplayable"
                          : segment.kind === "gap"
                            ? beforeStart
                              ? "Not started yet"
                              : "No signal"
                            : `${segment.chunks.length} chunks`,
                        lines: [
                          `${formatOffset(segment.start_ms)} – ${formatOffset(segment.end_ms)}`,
                          unplayable
                            ? "Recorded, but the segments that begin it never arrived — these bytes cannot be decoded"
                            : segment.kind === "chunks"
                              ? "Recorded"
                              : beforeStart
                                ? "This device was not recording before the exam started"
                                : `${SOURCE_LANE[track.source].subject} uploaded nothing for ${formatClock(segment.end_ms - segment.start_ms)}`,
                        ],
                      })
                    }
                  />
                );
              })}
            </div>
          </div>
        ))}

        {/* Proctor events */}
        <div className="mb-0.5 mt-2 text-2xs text-ink-muted">
          Proctor events
        </div>
        <div className="relative h-6 w-full rounded-xs border border-border bg-surface">
          {events.map((event) => (
            <button
              key={event.id}
              type="button"
              className={cn("tl-marker", SEVERITY_CLASS[event.severity])}
              style={{ left: `${pct(event.offset_ms)}%` }}
              aria-label={`${PROCTOR_EVENT_LABEL[event.type]} at ${formatOffset(event.offset_ms)}`}
              onClick={(e) => {
                e.stopPropagation();
                onSeek(event.offset_ms);
              }}
              onMouseEnter={() =>
                setHover({
                  left: pct(event.offset_ms),
                  title: PROCTOR_EVENT_LABEL[event.type],
                  lines: [
                    `${formatOffset(event.offset_ms)} · ${SEVERITY_LABEL[event.severity]}`,
                    ...describeMetadata(event.metadata),
                  ],
                })
              }
            />
          ))}
        </div>

        {/* Question opens */}
        <div className="mb-0.5 mt-2 text-2xs text-ink-muted">
          Questions opened
          {visits.length === 0 ? (
            <span className="ml-1.5 text-ink-faint">
              — no visit times recorded for this attempt
            </span>
          ) : (
            // Naming the two colours costs half a line and saves a legend.
            <span className="ml-1.5 text-ink-faint">
              — <span className="text-answered">green</span> right,{" "}
              <span className="text-violation">red</span> wrong
            </span>
          )}
        </div>
        <div className="relative h-6 w-full rounded-xs border border-border bg-surface">
          {visits.map((visit) => {
            const selected = selectedQuestionId === visit.question_id;
            return (
              <button
                key={visit.key}
                type="button"
                className={cn("tl-marker", ANSWER_CLASS[visit.state])}
                style={{
                  left: `${pct(visit.offset_ms)}%`,
                  // The selected question reads as a thicker tick rather than a
                  // different colour, because the colour is now carrying the
                  // grade and cannot be spent on selection as well.
                  ...(selected
                    ? { width: 5, marginLeft: -2.5, zIndex: 1 }
                    : null),
                }}
                aria-label={`Question ${visit.number}, ${ANSWER_LABEL[visit.state].toLowerCase()}, opened at ${formatClock(visit.offset_ms)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSeek(visit.offset_ms);
                  onSelectQuestion?.(visit.question_id, visit.offset_ms);
                }}
                onMouseEnter={() =>
                  setHover({
                    left: pct(visit.offset_ms),
                    title: `Question ${visit.number}`,
                    lines: [
                      `${formatClock(visit.offset_ms)} · ${ANSWER_LABEL[visit.state]}`,
                      visit.total_visits > 1
                        ? `Visit ${visit.visit} of ${visit.total_visits}`
                        : "Opened once",
                    ],
                  })
                }
              />
            );
          })}
        </div>

        {/* Where the exam actually began — the origin every offset is measured
            from, and the boundary between setup footage and exam footage. */}
        {hasSetup ? (
          <div
            className="pointer-events-none absolute inset-y-0 border-l border-dashed border-border-strong"
            style={{ left: `${pct(0)}%` }}
            aria-hidden
          />
        ) : null}

        {/* Playhead, drawn over every lane */}
        <div
          className="pointer-events-none absolute inset-y-0 w-px bg-ink"
          style={{ left: `${pct(timeMs)}%` }}
          aria-hidden
        >
          <div className="absolute -left-1 -top-1 size-2 rounded-full bg-ink" />
        </div>

        {hover ? (
          <div
            className="pointer-events-none absolute bottom-full z-10 mb-1 w-56 -translate-x-1/2 rounded-md border border-border bg-surface px-2.5 py-1.5 shadow-overlay"
            style={{
              // Keep the card inside the axis at both ends.
              left: `${Math.min(92, Math.max(8, hover.left))}%`,
            }}
          >
            <p className="text-2xs font-medium text-ink">{hover.title}</p>
            {hover.lines.map((line, i) => (
              <p key={i} className="tabular text-2xs text-ink-muted">
                {line}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Turns an event's metadata blob into one or two readable lines. */
function describeMetadata(metadata: Record<string, unknown> | null): string[] {
  if (!metadata) return [];
  const out: string[] = [];
  for (const [key, value] of Object.entries(metadata)) {
    if (value === null || value === undefined) continue;
    const label = key.replace(/_/g, " ");
    if (/_ms$/.test(key) && typeof value === "number") {
      out.push(`${label.replace(/ ms$/, "")}: ${formatClock(value)}`);
    } else {
      out.push(`${label}: ${String(value)}`);
    }
    if (out.length === 2) break;
  }
  return out;
}
